// Static IATA → geo/country registry for the /disruptions situation board.
// Presentation data only (marker placement + the region scope grouping) — never
// an operational source of truth. Airports missing here still render: the board
// puts them in the off-map tray, so unknown codes degrade visibly, not silently.

export interface AirportGeo {
  code: string;
  city: string;
  country: string;
  lat: number;
  lng: number;
}

function a(code: string, city: string, country: string, lat: number, lng: number): AirportGeo {
  return { code, city, country, lat, lng };
}

/** Airports with a place on one of the silhouettes (South America; Afro-Eurasia for
 *  Etihad; North America for Southwest; both shores of the Atlantic for Iberia). */
export const AIRPORT_GEO: Record<string, AirportGeo> = Object.fromEntries(
  [
    // Argentina
    a('AEP', 'Buenos Aires', 'Argentina', -34.56, -58.42),
    a('EZE', 'Buenos Aires', 'Argentina', -34.82, -58.54),
    a('COR', 'Córdoba', 'Argentina', -31.31, -64.21),
    a('MDZ', 'Mendoza', 'Argentina', -32.83, -68.79),
    a('SLA', 'Salta', 'Argentina', -24.86, -65.49),
    a('IGR', 'Iguazú', 'Argentina', -25.74, -54.47),
    a('BRC', 'Bariloche', 'Argentina', -41.15, -71.16),
    a('USH', 'Ushuaia', 'Argentina', -54.84, -68.3),
    // Brazil
    a('GRU', 'São Paulo', 'Brazil', -23.43, -46.47),
    a('CGH', 'São Paulo', 'Brazil', -23.63, -46.66),
    a('VCP', 'Campinas', 'Brazil', -23.01, -47.13),
    a('GIG', 'Rio de Janeiro', 'Brazil', -22.81, -43.25),
    a('SDU', 'Rio de Janeiro', 'Brazil', -22.91, -43.16),
    a('BSB', 'Brasília', 'Brazil', -15.87, -47.92),
    a('CNF', 'Belo Horizonte', 'Brazil', -19.62, -43.97),
    a('SSA', 'Salvador', 'Brazil', -12.91, -38.32),
    a('REC', 'Recife', 'Brazil', -8.13, -34.92),
    a('FOR', 'Fortaleza', 'Brazil', -3.78, -38.53),
    a('NAT', 'Natal', 'Brazil', -5.77, -35.37),
    a('BEL', 'Belém', 'Brazil', -1.38, -48.48),
    a('MAO', 'Manaus', 'Brazil', -3.04, -60.05),
    a('POA', 'Porto Alegre', 'Brazil', -29.99, -51.17),
    a('CWB', 'Curitiba', 'Brazil', -25.53, -49.18),
    a('FLN', 'Florianópolis', 'Brazil', -27.67, -48.55),
    // Chile
    a('SCL', 'Santiago', 'Chile', -33.39, -70.79),
    a('ANF', 'Antofagasta', 'Chile', -23.44, -70.44),
    a('CJC', 'Calama', 'Chile', -22.5, -68.9),
    a('IQQ', 'Iquique', 'Chile', -20.55, -70.18),
    a('LSC', 'La Serena', 'Chile', -29.92, -71.2),
    a('CCP', 'Concepción', 'Chile', -36.77, -73.06),
    a('PMC', 'Puerto Montt', 'Chile', -41.44, -73.09),
    a('PUQ', 'Punta Arenas', 'Chile', -53.0, -70.85),
    // Peru
    a('LIM', 'Lima', 'Peru', -12.02, -77.11),
    a('CUZ', 'Cusco', 'Peru', -13.54, -71.94),
    a('AQP', 'Arequipa', 'Peru', -16.34, -71.57),
    // Colombia
    a('BOG', 'Bogotá', 'Colombia', 4.7, -74.15),
    a('MDE', 'Medellín', 'Colombia', 6.16, -75.42),
    a('CTG', 'Cartagena', 'Colombia', 10.44, -75.51),
    a('CLO', 'Cali', 'Colombia', 3.54, -76.38),
    a('BAQ', 'Barranquilla', 'Colombia', 10.89, -74.78),
    // Ecuador
    a('UIO', 'Quito', 'Ecuador', -0.13, -78.36),
    a('GYE', 'Guayaquil', 'Ecuador', -2.16, -79.88),
    // Uruguay / Paraguay / Bolivia / Venezuela
    a('MVD', 'Montevideo', 'Uruguay', -34.84, -56.03),
    a('ASU', 'Asunción', 'Paraguay', -25.24, -57.52),
    a('LPB', 'La Paz', 'Bolivia', -16.51, -68.19),
    a('VVI', 'Santa Cruz', 'Bolivia', -17.64, -63.14),
    a('CCS', 'Caracas', 'Venezuela', 10.6, -66.99),
    // Valencia VENEZUELA (Arturo Michelena). Valencia SPAIN is VLC, in the Iberia
    // block below at 39.49 / -0.48 — same city name, 7 000 km apart.
    a('VLN', 'Valencia', 'Venezuela', 10.15, -67.93),
    // ── Etihad (EY) network — AUH hub, Gulf / subcontinent fan, long-haul gateways ──
    // Gulf / Middle East
    a('AUH', 'Abu Dhabi', 'United Arab Emirates', 24.43, 54.65),
    a('DOH', 'Doha', 'Qatar', 25.27, 51.61),
    a('BAH', 'Bahrain', 'Bahrain', 26.27, 50.63),
    a('KWI', 'Kuwait', 'Kuwait', 29.23, 47.97),
    a('MCT', 'Muscat', 'Oman', 23.59, 58.28),
    a('RUH', 'Riyadh', 'Saudi Arabia', 24.96, 46.70),
    a('JED', 'Jeddah', 'Saudi Arabia', 21.68, 39.16),
    a('DMM', 'Dammam', 'Saudi Arabia', 26.47, 49.80),
    a('AMM', 'Amman', 'Jordan', 31.72, 35.99),
    a('BEY', 'Beirut', 'Lebanon', 33.82, 35.49),
    a('CAI', 'Cairo', 'Egypt', 30.12, 31.41),
    // Asia
    a('BOM', 'Mumbai', 'India', 19.09, 72.87),
    a('DEL', 'Delhi', 'India', 28.56, 77.10),
    a('MAA', 'Chennai', 'India', 12.99, 80.17),
    a('HYD', 'Hyderabad', 'India', 17.24, 78.43),
    a('BLR', 'Bengaluru', 'India', 13.20, 77.71),
    a('COK', 'Kochi', 'India', 10.15, 76.40),
    a('KHI', 'Karachi', 'Pakistan', 24.91, 67.16),
    a('LHE', 'Lahore', 'Pakistan', 31.52, 74.40),
    a('CMB', 'Colombo', 'Sri Lanka', 7.18, 79.88),
    a('DAC', 'Dhaka', 'Bangladesh', 23.84, 90.40),
    a('KTM', 'Kathmandu', 'Nepal', 27.70, 85.36),
    a('MLE', 'Malé', 'Maldives', 4.19, 73.53),
    a('BKK', 'Bangkok', 'Thailand', 13.69, 100.75),
    a('KUL', 'Kuala Lumpur', 'Malaysia', 2.75, 101.71),
    a('SIN', 'Singapore', 'Singapore', 1.36, 103.99),
    a('CGK', 'Jakarta', 'Indonesia', -6.13, 106.66),
    a('MNL', 'Manila', 'Philippines', 14.51, 121.02),
    a('HKG', 'Hong Kong', 'Hong Kong', 22.31, 113.92),
    a('ICN', 'Seoul', 'South Korea', 37.46, 126.44),
    a('NRT', 'Tokyo', 'Japan', 35.77, 140.39),
    a('PVG', 'Shanghai', 'China', 31.14, 121.81),
    // Europe
    a('IST', 'Istanbul', 'Türkiye', 41.27, 28.75),
    a('LHR', 'London', 'United Kingdom', 51.47, -0.45),
    a('MAN', 'Manchester', 'United Kingdom', 53.35, -2.27),
    a('DUB', 'Dublin', 'Ireland', 53.42, -6.27),
    a('CDG', 'Paris', 'France', 49.01, 2.55),
    a('FRA', 'Frankfurt', 'Germany', 50.03, 8.57),
    a('MUC', 'Munich', 'Germany', 48.35, 11.79),
    a('AMS', 'Amsterdam', 'Netherlands', 52.31, 4.77),
    a('MXP', 'Milan', 'Italy', 45.63, 8.72),
    a('FCO', 'Rome', 'Italy', 41.80, 12.24),
    a('MAD', 'Madrid', 'Spain', 40.47, -3.56),
    a('LIS', 'Lisbon', 'Portugal', 38.77, -9.13),
    a('MIA', 'Miami', 'United States', 25.80, -80.29),
    a('BCN', 'Barcelona', 'Spain', 41.30, 2.08),
    a('ATH', 'Athens', 'Greece', 37.94, 23.94),
    a('ZRH', 'Zurich', 'Switzerland', 47.46, 8.55),
    // Oceania
    a('MEL', 'Melbourne', 'Australia', -37.67, 144.84),
    a('SYD', 'Sydney', 'Australia', -33.95, 151.18),
    // North America
    a('JFK', 'New York', 'United States', 40.64, -73.78),
    a('ORD', 'Chicago', 'United States', 41.98, -87.91),
    a('BOS', 'Boston', 'United States', 42.37, -71.02),
    a('YYZ', 'Toronto', 'Canada', 43.68, -79.63),
    // Africa
    a('JNB', 'Johannesburg', 'South Africa', -26.14, 28.25),
    a('NBO', 'Nairobi', 'Kenya', -1.32, 36.93),
    a('CMN', 'Casablanca', 'Morocco', 33.37, -7.59),
    a('ADD', 'Addis Ababa', 'Ethiopia', 8.98, 38.80),
    // ── Southwest (WN) network — US point-to-point across eight bases, plus the
    // Hawaii ETOPS turns and the Mexico / Caribbean leisure fan ──
    // Bases (the eight biggest, by departures)
    a('MDW', 'Chicago/Midway', 'United States', 41.79, -87.75),
    a('LAS', 'Las Vegas', 'United States', 36.08, -115.15),
    a('DEN', 'Denver', 'United States', 39.86, -104.67),
    a('BWI', 'Baltimore', 'United States', 39.17, -76.67),
    a('DAL', 'Dallas/Love Field', 'United States', 32.85, -96.85),
    a('PHX', 'Phoenix', 'United States', 33.44, -112.01),
    a('HOU', 'Houston/Hobby', 'United States', 29.65, -95.28),
    a('MCO', 'Orlando', 'United States', 28.43, -81.31),
    // West Coast / Pacific
    a('LAX', 'Los Angeles', 'United States', 33.94, -118.41),
    a('OAK', 'Oakland', 'United States', 37.72, -122.22),
    a('SAN', 'San Diego', 'United States', 32.73, -117.19),
    a('SJC', 'San Jose', 'United States', 37.36, -121.93),
    a('SMF', 'Sacramento', 'United States', 38.70, -121.59),
    a('BUR', 'Burbank', 'United States', 34.20, -118.36),
    a('ONT', 'Ontario', 'United States', 34.06, -117.60),
    a('SNA', 'Santa Ana', 'United States', 33.68, -117.87),
    a('RNO', 'Reno', 'United States', 39.50, -119.77),
    a('SEA', 'Seattle', 'United States', 47.45, -122.31),
    a('PDX', 'Portland', 'United States', 45.59, -122.60),
    // Mountain West
    a('SLC', 'Salt Lake City', 'United States', 40.79, -111.98),
    a('ABQ', 'Albuquerque', 'United States', 35.04, -106.61),
    // Advanced Air (AN) outstations — small mountain and coastal fields.
    a('HHR', 'Hawthorne', 'United States', 33.92, -118.33),
    a('CLD', 'Carlsbad', 'United States', 33.13, -117.28),
    a('CEC', 'Crescent City', 'United States', 41.78, -124.24),
    a('MMH', 'Mammoth Lakes', 'United States', 37.62, -118.84),
    a('AXX', 'Angel Fire', 'United States', 36.42, -105.29),
    a('SVC', 'Silver City', 'United States', 32.64, -108.16),
    a('GUP', 'Gallup', 'United States', 35.51, -108.79),
    // Air Mauritius (MK) network — the Indian Ocean stations and the ends of its fan.
    a('MRU', 'Mauritius', 'Mauritius', -20.43, 57.68),
    a('RRG', 'Rodrigues', 'Mauritius', -19.76, 63.36),
    a('RUN', 'Saint-Denis', 'Réunion (France)', -20.89, 55.52),
    a('TNR', 'Antananarivo', 'Madagascar', -18.8, 47.48),
    a('CPT', 'Cape Town', 'South Africa', -33.97, 18.6),
    a('GVA', 'Geneva', 'Switzerland', 46.24, 6.11),
    a('LGW', 'London/Gatwick', 'United Kingdom', 51.15, -0.19),
    a('PER', 'Perth', 'Australia', -31.94, 115.97),
    a('ELP', 'El Paso', 'United States', 31.81, -106.38),
    // Texas · Central
    a('AUS', 'Austin', 'United States', 30.19, -97.67),
    a('SAT', 'San Antonio', 'United States', 29.53, -98.47),
    a('OKC', 'Oklahoma City', 'United States', 35.39, -97.60),
    a('MSY', 'New Orleans', 'United States', 29.99, -90.26),
    a('STL', 'St. Louis', 'United States', 38.75, -90.37),
    a('MCI', 'Kansas City', 'United States', 39.30, -94.71),
    a('BNA', 'Nashville', 'United States', 36.13, -86.68),
    a('MSP', 'Minneapolis', 'United States', 44.88, -93.22),
    a('MKE', 'Milwaukee', 'United States', 42.95, -87.90),
    // Midwest · Northeast
    a('DTW', 'Detroit', 'United States', 42.21, -83.35),
    a('CLE', 'Cleveland', 'United States', 41.41, -81.85),
    a('CMH', 'Columbus', 'United States', 40.00, -82.89),
    a('IND', 'Indianapolis', 'United States', 39.72, -86.29),
    a('PIT', 'Pittsburgh', 'United States', 40.49, -80.23),
    a('LGA', 'New York/LaGuardia', 'United States', 40.78, -73.87),
    a('DCA', 'Washington/National', 'United States', 38.85, -77.04),
    a('PVD', 'Providence', 'United States', 41.72, -71.43),
    a('BDL', 'Hartford', 'United States', 41.94, -72.68),
    // Southeast · Florida · Puerto Rico
    a('ATL', 'Atlanta', 'United States', 33.64, -84.43),
    a('RDU', 'Raleigh/Durham', 'United States', 35.88, -78.79),
    a('TPA', 'Tampa', 'United States', 27.98, -82.53),
    a('FLL', 'Fort Lauderdale', 'United States', 26.07, -80.15),
    a('JAX', 'Jacksonville', 'United States', 30.49, -81.69),
    a('RSW', 'Fort Myers', 'United States', 26.54, -81.75),
    a('PBI', 'West Palm Beach', 'United States', 26.68, -80.10),
    a('SJU', 'San Juan', 'United States', 18.44, -66.00),
    // Hawaii (ETOPS overwater)
    a('HNL', 'Honolulu', 'United States', 21.32, -157.92),
    a('OGG', 'Kahului', 'United States', 20.90, -156.43),
    // Mexico · Central America · Caribbean
    a('CUN', 'Cancún', 'Mexico', 21.04, -86.88),
    a('SJD', 'Los Cabos', 'Mexico', 23.15, -109.72),
    a('PVR', 'Puerto Vallarta', 'Mexico', 20.68, -105.25),
    a('MBJ', 'Montego Bay', 'Jamaica', 18.50, -77.91),
    a('NAS', 'Nassau', 'Bahamas', 25.04, -77.47),
    a('SJO', 'San José', 'Costa Rica', 9.99, -84.21),
    // ── Iberia (IB) network — MAD hub; Spain, Europe, North Africa and the
    // Caribbean / Central-American fan ──
    // Spain beyond Madrid (Canaries keep their own zone)
    a('AGP', 'Málaga', 'Spain', 36.67, -4.50),
    a('PMI', 'Palma', 'Spain', 39.55, 2.74),
    a('SVQ', 'Seville', 'Spain', 37.42, -5.90),
    a('VLC', 'Valencia', 'Spain', 39.49, -0.48),
    a('BIO', 'Bilbao', 'Spain', 43.30, -2.91),
    a('ALC', 'Alicante', 'Spain', 38.28, -0.56),
    a('SCQ', 'Santiago', 'Spain', 42.90, -8.41),
    a('VGO', 'Vigo', 'Spain', 42.23, -8.63),
    a('IBZ', 'Ibiza', 'Spain', 38.87, 1.37),
    a('LPA', 'Las Palmas', 'Spain', 27.93, -15.39),
    a('TFN', 'Tenerife', 'Spain', 28.48, -16.34),
    a('ACE', 'Lanzarote', 'Spain', 28.95, -13.61),
    // Europe
    a('OPO', 'Porto', 'Portugal', 41.25, -8.68),
    a('ORY', 'Paris/Orly', 'France', 48.72, 2.38),
    a('BRU', 'Brussels', 'Belgium', 50.90, 4.48),
    a('DUS', 'Düsseldorf', 'Germany', 51.29, 6.77),
    a('BER', 'Berlin', 'Germany', 52.36, 13.50),
    a('VIE', 'Vienna', 'Austria', 48.11, 16.57),
    a('CPH', 'Copenhagen', 'Denmark', 55.62, 12.66),
    a('ARN', 'Stockholm', 'Sweden', 59.65, 17.92),
    a('NAP', 'Naples', 'Italy', 40.89, 14.29),
    a('PRG', 'Prague', 'Czechia', 50.10, 14.26),
    a('WAW', 'Warsaw', 'Poland', 52.17, 20.97),
    a('EDI', 'Edinburgh', 'United Kingdom', 55.95, -3.37),
    // North Africa · Middle East
    a('TNG', 'Tangier', 'Morocco', 35.73, -5.92),
    a('RAK', 'Marrakesh', 'Morocco', 31.61, -8.04),
    a('ALG', 'Algiers', 'Algeria', 36.69, 3.21),
    a('TLV', 'Tel Aviv', 'Israel', 32.01, 34.89),
    // Mexico · Central America · Caribbean
    a('PTY', 'Panama City', 'Panama', 9.07, -79.38),
    a('SAL', 'San Salvador', 'El Salvador', 13.44, -89.06),
    a('GUA', 'Guatemala City', 'Guatemala', 14.58, -90.53),
    a('HAV', 'Havana', 'Cuba', 22.99, -82.41),
    a('SDQ', 'Santo Domingo', 'Dominican Republic', 18.43, -69.67),
    a('MEX', 'Mexico City', 'Mexico', 19.44, -99.07),
    a('MTY', 'Monterrey', 'Mexico', 25.78, -100.11),
    // North America
    a('SFO', 'San Francisco', 'United States', 37.62, -122.38),
    a('DFW', 'Dallas', 'United States', 32.90, -97.04),
    a('IAD', 'Washington/Dulles', 'United States', 38.94, -77.46),
  ].map((g) => [g.code, g]),
);

export function airportGeo(code: string): AirportGeo | undefined {
  return AIRPORT_GEO[code.toUpperCase()];
}

/** Country for the region scope; undefined for off-map codes (they group as "Other"). */
export function countryOf(code: string): string | undefined {
  return airportGeo(code)?.country;
}

// ── Map frames ──────────────────────────────────────────────────────────────
// Equirectangular frame around the network; linear is fine for a stylized
// silhouette. viewBox is 10 units per degree.
//
// One frame per network shape. The South-American carriers (LATAM, SKY,
// Avianca) share the continent silhouette; Etihad's AUH-centred network gets
// the Afro-Eurasian one. The frame is picked by VITE_TENANT at build time — the
// same pin that selects the brand pack — so the board is never the wrong
// continent for the airline it is built for. An airport whose geo is known but
// falls outside the active frame is OFF the map (tray), exactly like an
// unknown code: it degrades visibly rather than projecting into nowhere.

/** A closed coastline ring of `[lat, lng]` points. Stylized on purpose — a
 *  recognizable silhouette, not cartography. */
type Ring = Array<[number, number]>;

export type MapFrameId = 'south-america' | 'afro-eurasia' | 'north-america' | 'atlantic' | 'indian-ocean';

export interface MapFrame {
  id: MapFrameId;
  lngMin: number;
  lngMax: number;
  /** South edge. */
  latMin: number;
  /** North edge. */
  latMax: number;
  outlines: ReadonlyArray<Ring>;
}

// Simplified South-America coastline, clockwise from the Darién gap.
const SOUTH_AMERICA_OUTLINE: Ring = [
  [8.9, -77.3],
  [11.0, -74.2],
  [12.4, -71.6],
  [10.6, -66.9],
  [10.0, -61.9],
  [8.4, -59.9],
  [6.8, -57.1],
  [5.7, -53.9],
  [4.2, -51.6],
  [1.0, -49.9],
  [-0.6, -47.8],
  [-1.2, -44.7],
  [-2.8, -41.8],
  [-4.8, -37.2],
  [-5.1, -35.2],
  [-7.0, -34.8],
  [-9.0, -35.7],
  [-13.0, -38.5],
  [-15.7, -38.9],
  [-18.3, -39.7],
  [-21.0, -40.9],
  [-23.0, -42.0],
  [-23.9, -46.3],
  [-25.5, -48.5],
  [-28.5, -48.8],
  [-32.1, -52.1],
  [-34.0, -53.5],
  [-34.9, -56.2],
  [-34.5, -58.4],
  [-36.3, -56.7],
  [-38.0, -57.5],
  [-38.9, -62.1],
  [-42.5, -63.6],
  [-45.9, -67.5],
  [-47.7, -65.7],
  [-50.0, -68.4],
  [-52.3, -68.4],
  [-53.8, -67.3],
  [-55.0, -66.9],
  [-53.9, -71.5],
  [-52.0, -73.5],
  [-49.5, -74.5],
  [-46.5, -74.2],
  [-43.0, -73.7],
  [-41.5, -73.8],
  [-38.5, -73.5],
  [-36.5, -72.9],
  [-33.0, -71.7],
  [-29.5, -71.4],
  [-26.0, -70.7],
  [-23.0, -70.5],
  [-19.5, -70.4],
  [-18.4, -70.3],
  [-16.5, -73.3],
  [-14.0, -76.2],
  [-12.0, -77.2],
  [-9.0, -78.6],
  [-6.0, -81.0],
  [-4.6, -81.3],
  [-3.0, -80.3],
  [-1.0, -80.9],
  [0.5, -80.1],
  [1.5, -79.0],
  [3.5, -77.4],
  [6.0, -77.4],
];

// Afro-Eurasia as one ring: Iberia → the North Sea and Baltic coasts →
// Scandinavia → the Arctic (mostly above the frame) → the Pacific seaboard →
// south-east Asia → the subcontinent → Arabia → the Red Sea into Africa →
// around the Cape → the Mediterranean back to Gibraltar. Clockwise.
const AFRO_EURASIA_OUTLINE: Ring = [
  [43.4, -8.4], [43.5, -5.0], [43.4, -1.8], [45.6, -1.2], [47.3, -2.5], [48.6, -4.7],
  [49.6, -1.6], [49.3, 0.2], [50.9, 1.6], [51.4, 3.3], [52.9, 4.7], [53.4, 6.9],
  [53.9, 8.7], [55.0, 8.4], [57.1, 8.6], [57.7, 10.6], [56.1, 10.2], [54.3, 10.1],
  [54.4, 11.0], [54.1, 13.8], [54.2, 16.2], [54.4, 19.6], [55.3, 21.1], [56.4, 21.0],
  [57.0, 24.1], [58.4, 23.5], [59.4, 24.7], [59.4, 28.0], [59.9, 30.3], [60.2, 24.9],
  [60.4, 22.2], [63.1, 21.6], [65.0, 25.4], [65.6, 22.2], [63.8, 20.3], [62.4, 17.3],
  [59.3, 18.1], [56.7, 16.4], [55.4, 13.0], [57.7, 11.9], [59.9, 10.7], [58.0, 7.0],
  [60.4, 5.3], [62.5, 6.1], [64.5, 10.5], [67.3, 14.4], [69.6, 18.9], [71.1, 25.8],
  [70.0, 30.0], [69.0, 33.0], [66.5, 41.0], [68.0, 46.0], [68.5, 54.0], [70.0, 60.0],
  [72.5, 70.0], [73.0, 80.0], [76.0, 100.0], [76.5, 112.0], [73.0, 122.0], [71.0, 132.0],
  [70.0, 150.0], [69.5, 160.0], [61.0, 164.0], [56.0, 163.0], [51.5, 157.0], [59.0, 153.0],
  [59.3, 143.0], [54.0, 138.0], [51.0, 140.5], [48.0, 140.0], [43.1, 131.9], [42.3, 130.6],
  [39.5, 127.6], [37.5, 129.4], [35.1, 129.0], [34.5, 126.5], [35.5, 126.5], [37.7, 126.4],
  [38.6, 125.0], [39.7, 124.3], [39.0, 121.7], [40.7, 121.1], [39.0, 118.0], [37.5, 122.0],
  [36.0, 120.4], [34.5, 119.5], [32.0, 121.5], [31.0, 121.9], [30.0, 122.0], [28.0, 121.2],
  [26.0, 119.6], [24.5, 118.3], [23.0, 116.5], [22.3, 114.1], [21.5, 111.5], [21.6, 108.5],
  [20.5, 106.6], [18.0, 106.0], [16.0, 108.3], [13.0, 109.3], [11.0, 108.9], [10.4, 107.0],
  [8.6, 104.8], [10.4, 104.2], [12.5, 102.0], [13.5, 100.5], [12.5, 99.9], [10.0, 99.2],
  [7.5, 100.5], [6.4, 101.9], [4.5, 103.4], [2.0, 103.8], [1.3, 103.6], [2.5, 101.4],
  [4.2, 100.6], [5.4, 100.3], [6.8, 99.7], [8.0, 98.3], [10.5, 98.7], [13.0, 98.1],
  [15.5, 97.7], [16.8, 96.2], [16.0, 94.4], [18.5, 94.3], [20.5, 92.9], [21.5, 92.2],
  [22.3, 91.8], [22.5, 90.5], [21.9, 89.0], [20.3, 86.7], [17.7, 83.3], [16.0, 81.5],
  [14.5, 80.2], [13.0, 80.3], [10.5, 79.9], [9.3, 79.1], [8.1, 77.5], [9.9, 76.2],
  [12.9, 74.8], [15.4, 73.8], [18.9, 72.8], [20.9, 72.8], [21.6, 72.6], [22.3, 70.0],
  [22.9, 68.6], [23.6, 68.3], [24.8, 67.0], [25.3, 64.5], [25.2, 61.6], [25.4, 58.9],
  [26.6, 57.0], [27.0, 56.3], [28.0, 51.5], [30.0, 50.0], [30.4, 48.7], [29.4, 47.9],
  [28.5, 48.4], [27.2, 49.5], [26.4, 50.2], [26.1, 51.2], [25.2, 51.6], [24.6, 51.4],
  [24.2, 52.7], [24.5, 54.4], [25.1, 55.1], [25.8, 56.1], [26.3, 56.4], [25.6, 56.4],
  [24.5, 56.7], [23.6, 58.6], [22.5, 59.8], [20.5, 58.8], [19.0, 57.7], [17.0, 55.3],
  [17.0, 54.0], [15.9, 52.2], [14.5, 49.0], [12.8, 45.0], [12.7, 43.5], [14.8, 42.9],
  [16.9, 42.6], [19.5, 41.0], [21.5, 39.2], [24.1, 38.0], [26.5, 36.3], [28.0, 34.7],
  [29.5, 35.0], [28.6, 34.5], [27.8, 34.3], [29.0, 32.9], [30.0, 32.5], [28.5, 33.0],
  [27.2, 33.8], [25.0, 35.0], [22.0, 36.9], [19.6, 37.3], [15.6, 39.5], [13.0, 42.7],
  [11.6, 43.2], [10.5, 44.5], [11.5, 49.0], [11.9, 51.3], [10.0, 51.0], [7.0, 49.5],
  [4.5, 48.0], [2.0, 45.5], [-1.5, 41.5], [-4.0, 39.7], [-6.8, 39.3], [-9.0, 39.6],
  [-12.0, 40.5], [-15.0, 40.7], [-17.0, 39.0], [-19.5, 36.0], [-21.0, 35.5], [-23.0, 35.4],
  [-26.0, 32.9], [-28.6, 32.4], [-30.0, 31.0], [-32.5, 28.5], [-34.0, 25.6], [-34.8, 20.0],
  [-34.2, 18.4], [-32.0, 18.3], [-29.0, 16.6], [-26.5, 15.1], [-22.9, 14.5], [-19.0, 12.8],
  [-16.5, 11.8], [-12.3, 13.6], [-8.8, 13.2], [-6.0, 12.3], [-4.8, 11.8], [-1.0, 9.0],
  [0.5, 9.4], [2.3, 9.8], [4.0, 9.5], [4.5, 8.0], [4.3, 6.0], [6.4, 3.4],
  [6.1, 1.2], [5.1, -1.3], [4.8, -2.2], [5.3, -4.0], [4.4, -7.5], [6.3, -10.8],
  [8.5, -13.2], [9.5, -13.7], [11.5, -15.5], [12.5, -16.7], [14.7, -17.4], [16.0, -16.5],
  [18.2, -16.1], [20.9, -17.1], [23.8, -15.9], [26.5, -13.5], [28.5, -11.3], [31.0, -9.8],
  [33.6, -7.6], [34.2, -6.6], [35.8, -5.9], [35.2, -3.0], [35.3, -1.5], [36.8, 3.0],
  [37.0, 7.5], [37.3, 9.9], [36.8, 10.3], [35.5, 11.0], [34.0, 10.2], [33.1, 11.6],
  [32.9, 13.2], [31.0, 17.5], [30.4, 19.2], [32.1, 20.1], [32.9, 21.9], [32.6, 23.1],
  [31.5, 25.1], [31.2, 29.9], [31.3, 32.3], [31.1, 33.5], [31.3, 34.3], [32.5, 34.9],
  [33.9, 35.5], [35.5, 35.8], [36.6, 36.1], [36.7, 34.6], [36.9, 30.7], [36.5, 29.1],
  [36.7, 28.2], [37.3, 27.3], [38.4, 26.4], [39.5, 26.2], [40.2, 26.4], [41.0, 29.2],
  [41.2, 31.4], [41.4, 36.4], [41.0, 39.7], [41.6, 41.6], [43.0, 40.5], [44.5, 38.0],
  [44.7, 37.5], [45.3, 36.5], [44.5, 34.2], [44.6, 33.5], [45.2, 32.5], [46.0, 32.5],
  [46.5, 30.7], [45.2, 29.7], [44.2, 28.6], [43.2, 27.9], [42.5, 27.5], [41.5, 28.2],
  [41.2, 28.9], [40.7, 26.6], [40.6, 25.9], [40.9, 25.3], [40.8, 24.7], [40.6, 22.9],
  [39.9, 22.6], [38.9, 23.2], [37.9, 23.7], [37.4, 23.1], [36.5, 22.4], [36.9, 21.7],
  [37.7, 21.3], [38.3, 21.7], [39.2, 20.5], [39.6, 20.1], [40.5, 19.4], [41.3, 19.4],
  [42.0, 19.2], [42.6, 18.1], [43.5, 16.4], [44.1, 15.2], [45.3, 14.4], [45.7, 13.7],
  [45.4, 12.3], [44.7, 12.3], [43.6, 13.5], [42.4, 14.2], [41.9, 15.9], [41.1, 16.9],
  [40.4, 18.4], [39.8, 18.4], [40.4, 17.2], [39.5, 16.5], [38.4, 16.0], [38.1, 15.6],
  [38.9, 16.2], [40.0, 15.3], [40.8, 14.2], [41.2, 13.0], [41.7, 12.3], [42.4, 11.2],
  [43.5, 10.3], [44.1, 9.8], [44.4, 8.9], [43.9, 8.0], [43.7, 7.3], [43.1, 5.9],
  [43.3, 5.3], [42.7, 3.1], [41.4, 2.2], [41.0, 1.0], [40.6, 0.6], [39.5, -0.3],
  [38.3, -0.5], [37.6, -0.7], [36.7, -2.5], [36.7, -4.4], [36.1, -5.4], [36.5, -6.3],
  [37.0, -6.9], [37.0, -8.0], [37.0, -8.9], [38.4, -9.3], [38.7, -9.4], [39.4, -9.4],
  [40.2, -8.9], [41.2, -8.7], [42.2, -8.8], [42.9, -9.3],
];

const GREAT_BRITAIN: Ring = [
  [50.1, -5.7], [50.3, -4.2], [50.6, -2.5], [50.7, -1.3], [50.8, 0.3], [51.1, 1.4],
  [51.5, 0.8], [51.9, 1.3], [52.5, 1.7], [52.9, 0.5], [53.5, 0.1], [54.1, -0.2],
  [54.6, -1.1], [55.6, -1.6], [56.0, -2.6], [56.4, -2.8], [57.1, -2.1], [57.7, -1.9],
  [57.7, -4.0], [58.6, -3.1], [58.6, -5.0], [58.0, -5.4], [57.3, -5.8], [56.5, -6.0],
  [56.1, -5.6], [55.7, -4.9], [55.0, -5.2], [54.9, -3.4], [54.5, -3.6], [53.9, -3.0],
  [53.3, -3.1], [53.4, -4.4], [52.8, -4.7], [52.2, -4.1], [51.7, -5.2], [51.4, -3.3],
  [51.2, -3.5], [50.9, -4.5], [50.5, -5.0],
];
const IRELAND: Ring = [
  [55.3, -7.3], [55.2, -6.1], [54.5, -5.5], [54.0, -6.1], [53.3, -6.1], [52.3, -6.4],
  [51.6, -8.5], [51.5, -9.8], [52.5, -9.9], [53.3, -9.5], [54.3, -10.0], [54.7, -8.6],
];
const JAPAN_MAIN: Ring = [
  [31.3, 130.6], [32.7, 130.0], [33.9, 130.7], [34.4, 131.2], [35.5, 133.0], [36.6, 136.5],
  [37.9, 138.5], [39.6, 140.0], [41.2, 140.4], [41.4, 141.5], [40.0, 141.9], [38.3, 141.5],
  [37.0, 141.0], [35.7, 140.9], [35.3, 139.8], [34.7, 138.9], [34.6, 137.0], [33.5, 135.8],
  [34.4, 135.2], [33.9, 134.3], [32.8, 133.0], [33.2, 131.9], [31.5, 131.4],
];
const JAPAN_HOKKAIDO: Ring = [
  [41.8, 140.7], [43.2, 140.4], [45.4, 141.7], [44.0, 145.2], [43.0, 145.5], [42.0, 143.3],
];
const SRI_LANKA: Ring = [
  [9.8, 80.2], [8.5, 81.2], [7.0, 81.8], [6.0, 81.6], [5.9, 80.3], [7.0, 79.8], [8.5, 79.9],
];
const PHILIPPINES: Ring = [
  [18.6, 120.8], [18.3, 122.2], [16.2, 122.2], [15.0, 121.5], [14.0, 123.3], [12.5, 124.2],
  [11.0, 125.5], [9.0, 126.0], [6.5, 126.2], [5.6, 125.2], [7.0, 123.4], [7.8, 122.1],
  [9.5, 123.0], [10.8, 122.2], [12.2, 121.2], [13.7, 120.6], [14.6, 120.2], [16.1, 119.8],
];
const BORNEO: Ring = [
  [7.0, 117.0], [5.4, 119.2], [3.0, 118.0], [1.0, 119.0], [-1.5, 117.0], [-3.5, 116.0],
  [-4.0, 114.5], [-3.5, 111.0], [-2.5, 110.0], [0.5, 109.0], [2.2, 109.7], [3.5, 113.0],
  [5.0, 115.5], [6.5, 116.7],
];
const SUMATRA: Ring = [
  [5.8, 95.3], [5.2, 97.5], [3.5, 99.3], [1.5, 101.0], [0.0, 103.5], [-1.5, 104.5],
  [-3.5, 106.0], [-5.9, 105.7], [-5.0, 104.0], [-3.0, 101.5], [-1.0, 100.3], [1.0, 98.5],
  [3.5, 97.0],
];
const JAVA: Ring = [
  [-6.0, 105.9], [-6.0, 106.9], [-6.9, 110.5], [-6.8, 112.5], [-7.6, 114.4], [-8.6, 114.4],
  [-8.3, 111.0], [-7.8, 108.5], [-7.0, 106.0],
];
const NEW_GUINEA: Ring = [
  [-1.0, 131.0], [-0.5, 132.5], [-2.3, 134.0], [-2.5, 137.5], [-3.6, 141.0], [-6.0, 147.5],
  [-8.5, 148.5], [-10.5, 150.5], [-10.0, 147.5], [-8.0, 143.5], [-8.5, 138.0], [-6.5, 138.0],
  [-4.5, 135.0], [-2.5, 133.0],
];
const AUSTRALIA: Ring = [
  [-12.0, 131.0], [-11.2, 132.4], [-12.2, 136.8], [-15.0, 136.0], [-17.6, 140.8], [-12.5, 141.6],
  [-10.8, 142.4], [-14.0, 143.6], [-18.3, 146.3], [-21.0, 149.2], [-25.0, 152.9], [-28.2, 153.5],
  [-32.7, 152.3], [-34.0, 151.2], [-36.0, 150.0], [-37.7, 149.9], [-39.1, 146.4], [-38.3, 144.5],
  [-38.4, 141.0], [-35.0, 137.6], [-34.5, 135.5], [-32.5, 133.7], [-31.6, 130.5], [-33.0, 124.0],
  [-34.0, 121.0], [-35.0, 117.6], [-34.3, 115.1], [-32.0, 115.7], [-29.0, 114.9], [-26.0, 113.4],
  [-23.5, 113.7], [-21.8, 114.2], [-20.0, 119.5], [-18.0, 122.3], [-16.2, 123.5], [-14.5, 125.5],
  [-14.0, 127.5],
];
const MADAGASCAR: Ring = [
  [-12.0, 49.3], [-14.5, 50.3], [-16.0, 49.8], [-18.5, 49.4], [-22.0, 48.0], [-25.5, 47.1],
  [-25.0, 44.4], [-22.0, 43.3], [-19.0, 44.3], [-16.0, 45.3], [-14.0, 47.5],
];
const SICILY: Ring = [[38.2, 15.6], [37.5, 15.1], [36.7, 15.1], [37.3, 13.1], [38.0, 12.5], [38.2, 13.5]];
const SARDINIA: Ring = [[41.2, 9.2], [39.2, 9.6], [38.9, 8.9], [40.0, 8.4], [41.0, 8.3]];
const CORSICA: Ring = [[43.0, 9.4], [41.4, 9.2], [41.6, 8.7], [42.7, 8.6]];
const CYPRUS: Ring = [[35.6, 34.5], [34.6, 33.1], [35.2, 32.3]];
const CRETE: Ring = [[35.3, 23.5], [35.4, 26.3], [35.0, 25.5], [35.2, 23.5]];
const TAIWAN: Ring = [[25.3, 121.5], [22.0, 120.9], [23.0, 120.1], [25.0, 121.1]];
const HAINAN: Ring = [[20.1, 110.6], [18.2, 109.6], [19.6, 108.6]];


// Simplified North-America coastline for the Southwest (WN) console, clockwise
// from the Pacific Northwest: the 49th parallel across Canada, the Atlantic
// seaboard, the Gulf, Mexico, Central America to the Darién, then back up the
// Pacific. Same stylization budget as the other two frames — a recognizable
// silhouette, not cartography.
const NORTH_AMERICA_OUTLINE: Ring = [
  [49.0, -123.0], [49.0, -95.2], [48.1, -89.6], [46.5, -84.4], [45.0, -82.4],
  [42.1, -83.1], [42.9, -78.9], [44.1, -76.4], [45.0, -74.7], [45.3, -71.5],
  [45.9, -67.8], [44.7, -67.0], [43.7, -70.2], [42.3, -70.9], [41.4, -71.4],
  [41.0, -72.0], [40.6, -74.0], [39.3, -74.4], [38.9, -75.0], [37.0, -75.9],
  [35.9, -75.6], [34.7, -76.6], [33.9, -78.0], [32.8, -79.9], [31.1, -81.4],
  [30.3, -81.4], [28.5, -80.6], [26.7, -80.0], [25.2, -80.3], [24.6, -81.8],
  [25.9, -81.7], [27.9, -82.8], [29.7, -84.9], [30.4, -87.2], [30.2, -89.1],
  [29.1, -89.2], [29.7, -93.9], [28.0, -96.9], [26.1, -97.2], [22.3, -97.8],
  [19.2, -96.1], [18.5, -94.5], [18.7, -91.6], [21.3, -89.7], [21.6, -86.7],
  [20.6, -86.9], [19.6, -87.45], [18.5, -88.3], [16.0, -88.5], [15.9, -85.9], [14.0, -83.2],
  [11.0, -83.7], [9.6, -82.6], [9.4, -79.9], [9.0, -77.9], [8.0, -78.0],
  [7.5, -80.2], [8.6, -83.2], [9.9, -85.2], [12.9, -87.7], [13.5, -89.9],
  [15.7, -93.7], [16.2, -95.2], [16.8, -99.9], [19.1, -104.3], [20.7, -105.4],
  [23.2, -106.4], [26.9, -109.9], [31.3, -113.5], [32.5, -117.1], [34.0, -118.5],
  [35.4, -120.9], [36.6, -121.9], [37.8, -122.5], [40.4, -124.4], [43.3, -124.4],
  [46.2, -124.0], [47.9, -124.7],
];

// Baja is its own ring so the Gulf of California reads as water — the mainland
// ring above runs down its eastern shore.
const BAJA_CALIFORNIA: Ring = [
  [32.5, -117.1], [30.5, -116.0], [28.0, -114.5], [26.0, -112.5], [24.1, -110.95],
  [22.87, -109.68], [23.4, -109.42], [24.15, -110.31], [26.0, -111.3], [28.0, -112.8],
  [30.5, -114.6], [31.7, -114.8],
];
const CUBA: Ring = [
  [23.2, -82.4], [23.2, -80.5], [22.5, -78.5], [21.6, -77.0], [21.0, -75.6],
  [20.2, -74.1], [19.9, -75.2], [20.0, -77.0], [20.7, -78.5], [21.5, -80.0],
  [22.1, -81.5], [21.9, -84.9], [22.9, -84.0],
];
const HISPANIOLA: Ring = [
  [19.9, -71.7], [19.8, -69.9], [19.3, -68.9], [18.4, -68.4], [18.2, -69.9],
  [18.2, -71.7], [18.0, -73.0], [18.2, -74.5], [18.6, -73.5], [19.7, -72.8],
];
const JAMAICA: Ring = [
  [18.55, -78.4], [18.52, -77.3], [18.45, -76.3], [17.85, -76.2], [17.87, -77.4], [18.2, -78.35],
];
const PUERTO_RICO: Ring = [[18.5, -67.2], [18.5, -65.6], [17.95, -65.8], [17.95, -67.2]];
const ANDROS: Ring = [[25.2, -78.1], [24.7, -77.7], [23.9, -77.6], [24.2, -78.1], [25.0, -78.3]];
const GRAND_BAHAMA: Ring = [[26.75, -78.95], [26.6, -78.0], [26.5, -77.8], [26.65, -78.6]];
const NEW_PROVIDENCE: Ring = [[25.09, -77.55], [25.09, -77.2], [24.98, -77.25], [24.99, -77.55]];
// Iberia's island stations. Nothing in the three earlier frames draws them, so
// PMI/IBZ/LPA/TFN/ACE would sit in open water without these.
const MALLORCA: Ring = [[39.96, 3.15], [39.75, 3.47], [39.35, 3.25], [39.27, 2.95], [39.55, 2.35], [39.85, 2.7]];
const IBIZA: Ring = [[39.12, 1.45], [38.95, 1.63], [38.65, 1.45], [38.85, 1.22]];
// Las Palmas' field is right on the east coast, so this ring has to reach −15.33.
const GRAN_CANARIA: Ring = [[28.18, -15.42], [28.15, -15.36], [27.9, -15.33], [27.75, -15.6], [27.85, -15.83], [28.15, -15.72]];
const TENERIFE: Ring = [[28.58, -16.33], [28.4, -16.11], [28.0, -16.6], [28.25, -16.85], [28.5, -16.75]];
const LANZAROTE: Ring = [[29.24, -13.5], [29.0, -13.42], [28.85, -13.75], [29.05, -13.87]];
const LONG_ISLAND: Ring = [[41.0, -73.9], [41.15, -72.3], [41.05, -71.85], [40.6, -73.0], [40.6, -73.8]];

export const MAP_FRAMES: Readonly<Record<MapFrameId, MapFrame>> = {
  'south-america': {
    id: 'south-america',
    lngMin: -84,
    lngMax: -33,
    latMin: -57,
    latMax: 13,
    outlines: [SOUTH_AMERICA_OUTLINE],
  },
  // Iberia to Australia; the Arctic and the far Pacific fall off the edges.
  // North America is deliberately outside: those four Etihad gateways go to the
  // tray, which keeps the Gulf — where the operation lives — readable.
  'afro-eurasia': {
    id: 'afro-eurasia',
    lngMin: -20,
    lngMax: 155,
    latMin: -42,
    latMax: 62,
    outlines: [
      AFRO_EURASIA_OUTLINE, GREAT_BRITAIN, IRELAND, JAPAN_MAIN, JAPAN_HOKKAIDO, SRI_LANKA,
      PHILIPPINES, BORNEO, SUMATRA, JAVA, NEW_GUINEA, AUSTRALIA, MADAGASCAR, SICILY, SARDINIA,
      CORSICA, CYPRUS, CRETE, TAIWAN, HAINAN,
    ],
  },
  // The 49th parallel down to the Darién gap. Hawaii (lng ≈ −158) is deliberately
  // outside: stretching the frame west to reach HNL/OGG would shrink the mainland
  // — where the operation lives — to a strip. Those two go to the tray.
  'north-america': {
    id: 'north-america',
    lngMin: -126,
    lngMax: -64,
    latMin: 7,
    latMax: 50,
    outlines: [
      NORTH_AMERICA_OUTLINE, BAJA_CALIFORNIA, CUBA, HISPANIOLA, JAMAICA, PUERTO_RICO,
      ANDROS, GRAND_BAHAMA, NEW_PROVIDENCE, LONG_ISLAND,
    ],
  },
  // Both shores of the Atlantic — the shape of Iberia's network: a Madrid hub
  // feeding Europe and North Africa on one side and the Americas on the other.
  // Every ring here is REUSED from the three frames above; nothing new is drawn,
  // so this frame costs the bundle nothing beyond its own entry. Points east of
  // lng 40 and west of −102 simply project outside the viewBox and are clipped.
  // San Francisco and Los Angeles fall off the western edge into the tray, the
  // same trade the other two frames make at their own margins.
  // The Indian Ocean rim — the shape of Air Mauritius' network: an island hub
  // 2,000 km off Africa feeding Europe to the north-west, India and Malaysia to
  // the north-east, Perth to the east and southern Africa to the west. Every
  // ring is REUSED from the afro-eurasia frame; nothing new is drawn. Mauritius,
  // Rodrigues and Réunion are too small to outline at this scale — their markers
  // sit over open ocean east of Madagascar, which is exactly what an operator
  // there expects to see. Japan, China and the far Pacific are cut off on
  // purpose: they are not in the network, and keeping the frame this tight is
  // what makes the Mascarenes readable rather than a speck in a hemisphere.
  'indian-ocean': {
    id: 'indian-ocean',
    lngMin: -20,
    lngMax: 125,
    latMin: -42,
    latMax: 58,
    outlines: [
      AFRO_EURASIA_OUTLINE, GREAT_BRITAIN, IRELAND, SRI_LANKA, SUMATRA, JAVA, BORNEO,
      AUSTRALIA, MADAGASCAR, SICILY, SARDINIA, CORSICA, CYPRUS, CRETE,
    ],
  },
  atlantic: {
    id: 'atlantic',
    lngMin: -102,
    lngMax: 40,
    latMin: -40,
    latMax: 62,
    outlines: [
      NORTH_AMERICA_OUTLINE, BAJA_CALIFORNIA, SOUTH_AMERICA_OUTLINE, AFRO_EURASIA_OUTLINE,
      GREAT_BRITAIN, IRELAND, CUBA, HISPANIOLA, JAMAICA, PUERTO_RICO, ANDROS,
      GRAND_BAHAMA, NEW_PROVIDENCE, LONG_ISLAND, SICILY, SARDINIA, CORSICA, CRETE, CYPRUS,
      MALLORCA, IBIZA, GRAN_CANARIA, TENERIFE, LANZAROTE,
    ],
  },
};

/** Which silhouette an airline's console draws. Unknown tenants keep the pilot's. */
const FRAME_BY_TENANT: Readonly<Record<string, MapFrameId>> = {
  latam: 'south-america',
  avianca: 'south-america',
  sky: 'south-america',
  etihad: 'afro-eurasia',
  southwest: 'north-america',
  iberia: 'atlantic',
  // Plus Ultra: MAD hub + TFN, and a South-American / Caribbean fan (LIM, EZE,
  // CCS, VLN, BOG, CTG, MVD, NAT). That is a strict subset of Iberia's shape, so
  // it draws the same both-shores-of-the-Atlantic silhouette. Every PU station
  // sits inside this frame's box — none go to the tray.
  plusultra: 'atlantic',
  // Advanced Air's ten stations are California / New Mexico / Arizona — all inside
  // the 49th-parallel frame Southwest already draws.
  advancedair: 'north-america',
  airmauritius: 'indian-ocean',
};

export function frameForTenant(tenant: string | undefined): MapFrame {
  return MAP_FRAMES[FRAME_BY_TENANT[(tenant ?? '').toLowerCase()] ?? 'south-america'];
}

/** The frame this build draws — pinned by VITE_TENANT like the brand pack. */
export const MAP_FRAME: MapFrame = frameForTenant(import.meta.env.VITE_TENANT);

export const MAP_VIEW_W = (MAP_FRAME.lngMax - MAP_FRAME.lngMin) * 10;
export const MAP_VIEW_H = (MAP_FRAME.latMax - MAP_FRAME.latMin) * 10;

export function projectIn(frame: MapFrame, lat: number, lng: number): { x: number; y: number } {
  return {
    x: ((lng - frame.lngMin) / (frame.lngMax - frame.lngMin)) * (frame.lngMax - frame.lngMin) * 10,
    y: ((frame.latMax - lat) / (frame.latMax - frame.latMin)) * (frame.latMax - frame.latMin) * 10,
  };
}

export function project(lat: number, lng: number): { x: number; y: number } {
  return projectIn(MAP_FRAME, lat, lng);
}

export function inFrame(frame: MapFrame, lat: number, lng: number): boolean {
  return lat >= frame.latMin && lat <= frame.latMax && lng >= frame.lngMin && lng <= frame.lngMax;
}

/** Geo-referenced AND inside the active frame — the board's "on the map" test. */
export function onActiveMap(code: string): boolean {
  const geo = airportGeo(code);
  return geo !== undefined && inFrame(MAP_FRAME, geo.lat, geo.lng);
}

function ringPath(frame: MapFrame, ring: Ring): string {
  return (
    ring
      .map(([lat, lng], i) => {
        const { x, y } = projectIn(frame, lat, lng);
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(' ') + ' Z'
  );
}

/** SVG paths of the active frame's silhouettes, in its projected coordinates. */
export const MAP_PATHS: ReadonlyArray<string> = MAP_FRAME.outlines.map((ring) => ringPath(MAP_FRAME, ring));

/** The South-America silhouette in its own frame (the board's original single path). */
export const SOUTH_AMERICA_PATH = ringPath(MAP_FRAMES['south-america'], SOUTH_AMERICA_OUTLINE);
